import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  ArrowLeft, Loader2, HelpCircle, Plus, Pencil, Save
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import RecordActions from '../../components/RecordActions';
import { AdminSubNav, SUBNAV_CONTENT } from '../../components/admin/AdminSubNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = { q: '', a: '', display_order: 0, is_published: true };

const AdminFAQsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/settings');
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchFaqs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/faqs`, { headers });
      setFaqs(res.data);
    } catch { toast.error('Failed to load FAQs'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchFaqs(); }, [fetchFaqs]);

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm, display_order: faqs.length }); setDialogOpen(true); };
  const openEdit = (f) => {
    setEditing(f);
    setForm({
      q: f.q || '',
      a: f.a || '',
      display_order: f.display_order ?? 0,
      is_published: f.is_published !== false,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.q.trim() || !form.a.trim()) {
      toast.error('Question and answer are required'); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, display_order: parseInt(form.display_order, 10) || 0 };
      if (editing) {
        await axios.put(`${API_URL}/api/admin/faqs/${editing.id}`, payload, { headers });
        toast.success('FAQ updated');
      } else {
        await axios.post(`${API_URL}/api/admin/faqs`, payload, { headers });
        toast.success('FAQ added');
      }
      setDialogOpen(false);
      fetchFaqs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const togglePublish = async (f) => {
    try {
      await axios.post(`${API_URL}/api/admin/faqs/${f.id}/toggle-publish`, {}, { headers });
      toast.success(f.is_published === false ? 'FAQ published' : 'FAQ unpublished');
      fetchFaqs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle');
    }
  };

  const publishedCount = faqs.filter(f => f.is_published !== false).length;
  const draftCount = faqs.length - publishedCount;

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-faqs-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" size="sm" onClick={back} className="text-zinc-500" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
          </Button>
          <Button onClick={openCreate} className="bg-[#F56F05] hover:bg-[#C2410C] text-white rounded-full" data-testid="add-faq-btn">
            <Plus className="h-4 w-4 mr-2" /> Add FAQ
          </Button>
        </div>

        <div className="flex items-center gap-3 mb-2" data-legacy-title>
          <HelpCircle className="h-6 w-6 text-[#F56F05]" />
          <h1 className="text-3xl font-semibold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>FAQs</h1>
        </div>
        <p className="text-sm text-zinc-500 mb-4">Manage the questions and answers shown on your website's FAQ section.</p>

        <AdminSubNav section="Content" items={SUBNAV_CONTENT} />

        <div className="flex flex-wrap gap-3 mb-6 mt-4">
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 rounded-full text-xs px-3 py-1" data-testid="published-count">
            {publishedCount} live on site
          </Badge>
          {draftCount > 0 && (
            <Badge variant="outline" className="bg-zinc-100 text-zinc-600 border-zinc-200 rounded-full text-xs px-3 py-1" data-testid="draft-count">
              {draftCount} draft{draftCount === 1 ? '' : 's'}
            </Badge>
          )}
        </div>

        {faqs.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-16 text-center">
              <HelpCircle className="h-12 w-12 mx-auto text-zinc-300 mb-4" />
              <p className="text-zinc-600 font-medium mb-1">No FAQs yet</p>
              <p className="text-zinc-400 text-sm mb-6">Add your first FAQ to display it on the website.</p>
              <Button onClick={openCreate} className="bg-[#F56F05] hover:bg-[#C2410C] text-white" data-testid="empty-add-faq-btn">
                <Plus className="h-4 w-4 mr-2" /> Add first FAQ
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <motion.div key={f.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}>
                <Card className={`border-0 shadow-sm hover:shadow-md transition-shadow ${f.is_published === false ? 'opacity-70' : ''}`} data-testid={`faq-row-${f.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-zinc-100 text-zinc-500 flex items-center justify-center font-semibold text-xs flex-shrink-0 mt-0.5">
                        #{(f.display_order ?? 0) + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <p className="font-semibold text-zinc-900">{f.q}</p>
                          {f.is_published === false && (
                            <Badge className="bg-zinc-200 text-zinc-700 border-zinc-300 text-[10px]" data-testid={`draft-badge-${f.id}`}>Draft</Badge>
                          )}
                        </div>
                        <p className="text-sm text-zinc-600 leading-relaxed">{f.a}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Switch
                          checked={f.is_published !== false}
                          onCheckedChange={() => togglePublish(f)}
                          data-testid={`toggle-publish-${f.id}`}
                        />
                        <Button variant="ghost" size="icon" onClick={() => openEdit(f)} data-testid={`edit-faq-${f.id}`}>
                          <Pencil className="h-4 w-4 text-zinc-400" />
                        </Button>
                        <RecordActions
                          canEdit={false}
                          canArchive={false}
                          canDelete={true}
                          deleteUrl={`/api/admin/faqs/${f.id}`}
                          deleteConfirmTitle="Delete this FAQ?"
                          deleteConfirmDescription="Permanently removes this FAQ from your website and database. This cannot be undone."
                          onDeleted={fetchFaqs}
                          headers={headers}
                          testIdPrefix="faq"
                          entityId={f.id}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="faq-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-[#F56F05]" />
              {editing ? 'Edit FAQ' : 'Add FAQ'}
            </DialogTitle>
            <DialogDescription>
              {editing ? 'Update this FAQ.' : 'Add a new question and answer to the FAQ section.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Question *</Label>
              <Input value={form.q} onChange={e => setForm({ ...form, q: e.target.value })} placeholder="e.g. What areas do you serve?" data-testid="faq-question" />
            </div>
            <div>
              <Label className="text-xs">Answer *</Label>
              <Textarea
                value={form.a}
                onChange={e => setForm({ ...form, a: e.target.value })}
                rows={5}
                placeholder="Provide a clear, helpful answer to the question."
                data-testid="faq-answer"
              />
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <Label className="text-xs">Display order (lower = higher)</Label>
                <Input type="number" value={form.display_order} onChange={e => setForm({ ...form, display_order: e.target.value })} data-testid="faq-order" />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} data-testid="faq-publish-switch" />
                <Label className="text-xs cursor-pointer" onClick={() => setForm({ ...form, is_published: !form.is_published })}>
                  {form.is_published ? 'Live on website' : 'Draft (hidden)'}
                </Label>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#F56F05] hover:bg-[#C2410C] text-white" onClick={save} disabled={saving} data-testid="faq-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {editing ? 'Save changes' : 'Add FAQ'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFAQsPage;
